'use client';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import organizationsService from '@/services/organizations-service';
import type { OrganizationData } from '@/services/organizations-service';
import { Building2, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

interface CreateOrganizationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onOrgCreated: (org: OrganizationData) => void;
}

/**
 * Dialog for creating a new organization.
 *
 * Fields:
 * - Organization name (required)
 *
 * On submit: calls POST /organizations, then invokes onOrgCreated callback.
 * The backend auto-generates a URL slug from the name.
 */
export function CreateOrganizationDialog({
  open,
  onOpenChange,
  onOrgCreated,
}: CreateOrganizationDialogProps) {
  const t = useTranslations('Organizations');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (open) {
      setName('');
      setIsSubmitting(false);
      setError(null);
    }
  }, [open]);

  const handleSubmit = useCallback(async () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    setIsSubmitting(true);
    setError(null);

    try {
      const response =
        await organizationsService.createOrganization(trimmedName);

      if (response.status === 'success' && response.data) {
        onOrgCreated(response.data);
      } else {
        setError(response.message ?? t('unexpectedError'));
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      const detail = axiosErr?.response?.data?.detail;
      if (
        detail?.includes('Users can belong to one organization') ||
        detail === 'already_in_org'
      ) {
        setError(t('alreadyInOrg'));
      } else {
        setError(detail ?? t('unexpectedError'));
      }
    } finally {
      setIsSubmitting(false);
    }
  }, [name, onOrgCreated, t]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && name.trim() && !isSubmitting) {
        void handleSubmit();
      }
    },
    [handleSubmit, name, isSubmitting]
  );

  const benefits = [
    t('createOrgBenefit1'),
    t('createOrgBenefit2'),
    t('createOrgBenefit3'),
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-md bg-surface-card border-line-default'>
        <DialogHeader>
          <div className='flex items-center gap-3'>
            <div
              className='flex h-10 w-10 items-center justify-center rounded-full
                bg-brand-tertiary-muted'
              aria-hidden='true'
            >
              <Building2 className='h-5 w-5 text-brand-tertiary' />
            </div>
            <div>
              <DialogTitle className='text-content-primary'>
                {t('createOrgTitle')}
              </DialogTitle>
              <DialogDescription className='text-content-secondary'>
                {t('createOrgDescription')}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className='space-y-4 py-2'>
          {/* Benefit list */}
          <ul className='space-y-1.5' aria-label={t('createOrgBenefitsLabel')}>
            {benefits.map((benefit) => (
              <li key={benefit} className='flex items-start gap-2'>
                <span
                  className='mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-brand-tertiary-muted'
                  aria-hidden='true'
                >
                  <svg
                    className='h-2.5 w-2.5 text-brand-tertiary'
                    fill='currentColor'
                    viewBox='0 0 8 8'
                  >
                    <circle cx='4' cy='4' r='3' />
                  </svg>
                </span>
                <span className='text-sm text-content-secondary'>
                  {benefit}
                </span>
              </li>
            ))}
          </ul>

          <div className='border-t border-line-subtle' />

          {/* Organization name */}
          <div className='space-y-2'>
            <Label
              htmlFor='org-name'
              className='text-sm font-medium text-content-primary'
            >
              {t('createOrgNameLabel')}
            </Label>
            <Input
              id='org-name'
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('createOrgNamePlaceholder')}
              className='border-line-default focus:ring-brand-tertiary/40'
              disabled={isSubmitting}
              autoFocus
            />
          </div>

          {/* Error message */}
          {error && (
            <p className='text-sm text-status-error' role='alert'>
              {error}
            </p>
          )}
        </div>

        <DialogFooter className='flex gap-2 sm:gap-2'>
          <Button
            variant='outline'
            className='min-h-[44px] border-line-default text-content-secondary'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            {t('cancelCreate')}
          </Button>
          <Button
            className='min-h-[44px] flex-1'
            onClick={() => void handleSubmit()}
            disabled={!name.trim() || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <Loader2
                  className='mr-2 h-4 w-4 animate-spin'
                  aria-hidden='true'
                />
                {t('createOrgSubmitting')}
              </>
            ) : (
              t('createOrgSubmit')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
